// Chat DAG — messages → {nodes, edges}.
//
// Walks ConversationContext's messages list and derives a per-turn
// subgraph: user_marker → llm_round_1 → fan-out of tool_calls →
// (consult_peers: further fan-out to peer nodes) → llm_round_2 → …
// → final synthesis round → assistant_marker. Turns are chained
// end-to-start via an edge from the previous assistant_marker to the
// next user_marker.
//
// Inputs intentionally shaped like the rest of the chat surface so
// this function composes cleanly with Wave 6 Task 24's metadata
// shape: `msg.metadata.tool_calls`, each with
// `timing.llm_round`, `identity.tool_call_id`, `identity.tool_name`,
// and `result` (which for consult_peers carries `peers[]`).

import type { Node, Edge } from 'reactflow';
import type { Message } from '../../../../contexts/ConversationContext';
import type { ToolCallEntry } from '../../ToolCallPill';
import type {
  CollapsedPhaseStackData,
  LlmRoundData,
  PeerDagData,
  PeerResponseData,
  SpineRootMarkerData,
  SquadMergeData,
  MergeNodeData,
  ToolCallDagData,
  TurnMarkerData,
} from '../types';
import { chatDagWarn } from './warnTelemetry';
import { BLUEPRINT_NODE_SIZE } from '../chatDagTokens';
import type { DagResponse } from '../../../../types/dag';
import { classifyAssistant } from './classifyMessage';
import { computeIntentSpineMembers } from './spineMembership';
import { buildIntentThread } from './intentThread';
import { buildChildrenByParent } from '../../childrenByParent';
import { campArrivalState } from '../../campArrivalState';
import { collectMergedCampIds } from '../../campMergedState';
import { nearestSpineRootId } from './resolveHillTarget';
import type { ResolvedHillTarget } from '../../../../types/hill';


// Re-exports from runtimeOverlay — Phase 2 T2.1 lift. Public API unchanged
// for downstream consumers (ChatDagCanvas, tests). Phase 4 cutover deletes
// this file in favor of a clean messagesToDag.ts that imports directly
// from runtimeOverlay.
import {
  EMPTY_RUNTIME as _EMPTY_RUNTIME,
  fanCollapsed as _fanCollapsed,
  phaseStackCollapsed,
  deriveStackName,
  isLatestTurnByIndex,
  coerceContentToString,
  toolCallId,
  toolName,
  unwrapResult,
  _emitArtifactTilesAndOrphans,
  orphanRunningSweep,
  errorTurnSweep,
  emitSpeculativeTurn,
} from './runtimeOverlay';
import type { RuntimeState as _RuntimeState } from './runtimeOverlay';
import type { ArtifactContentEntry } from '../types';

export type RuntimeState = _RuntimeState;
export const EMPTY_RUNTIME = _EMPTY_RUNTIME;
export const fanCollapsed = _fanCollapsed;
export {
  phaseStackCollapsed,
  deriveStackName,
  isLatestTurnByIndex,
  coerceContentToString,
  toolCallId,
  toolName,
  unwrapResult,
  _emitArtifactTilesAndOrphans,
  orphanRunningSweep,
  errorTurnSweep,
  emitSpeculativeTurn,
};

export function messagesToDag(
  messages: Message[],
  runtime: RuntimeState = EMPTY_RUNTIME,
  // Trailmap L1 (plan Task 13) — when present, emit artifact_tile nodes
  // bound to source_turn_id + orphan_tray node for NULL source_turn_id
  // artifacts. onTileClick is the canvas-level click handler that
  // mounts ArtifactViewer in the expansion panel (plan Task 15).
  dag?: DagResponse,
  onTileClick?: (artifactId: string) => void,
  // SP3a Hills — optional resolver (persona→spine→trail). When present, stamps
  // resolvedTarget on spine_root_marker + peer_response node data. Optional ⇒
  // every existing caller is unaffected.
  resolveTarget?: (
    personaId: string | undefined,
    spineRootMessageId: string | undefined,
  ) => ResolvedHillTarget | null,
  // Trailmap L2 Drip 3 — canvas-local fetched artifact bodies, keyed by id.
  // Threaded to the tile emit so expanded tiles carry their content.
  artifactContent?: ReadonlyMap<string, ArtifactContentEntry>,
): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  // C2.1 sp5 — Camp arrival state derives from server truth (the message graph +
  // the Camp's turn_failed metadata) at build time; recomputed on every reload.
  const _childrenByParent = buildChildrenByParent(messages);
  const _byId = new Map(messages.map((m) => [m.id, m]));
  // SP-3a — single membership authority. Computed ONCE here; the user/peer/
  // asst emit branches stamp intentSpineMember/intentSpineRootId on node data
  // and the layout pass reads the flag. messageId → owning spine-root id.
  const intentMembers = computeIntentSpineMembers(messages, _byId);
  // SP-3a — re-anchor-free suppression. A pure-message member (a foreman
  // peer_response, OR a user turn whose spine child is a peer) renders ONLY in
  // the spine_chat_panel; its canvas node + edges are DROPPED (never repointed
  // — the panel carries zero canvas edges). A canonical-asst member turn (and
  // therefore its user-turn parent) STAYS on the canvas with provenance intact
  // (approach B) and its text mirrors into the panel.
  const isPeerMsg = (m: any) => !!m && classifyAssistant(m) === 'peer_response';
  const suppressMember = (msg: any): boolean => {
    if (!intentMembers.has(msg.id)) return false;
    if (isPeerMsg(msg)) return true; // foreman peer / seed
    if (msg.role === 'user') {
      const kids = _childrenByParent.get(msg.id) ?? [];
      return kids.some((k: string) => isPeerMsg(_byId.get(k)));
    }
    return false; // canonical-asst (and its user parent) stay on canvas
  };
  // C2.2 — which Camps have been folded back (server truth, recomputed each build).
  const _mergedCampIds = collectMergedCampIds(messages);
  // C2.3 — turn ids a council_switchback re-blazed FROM (parent_ids[0] = the
  // reconsider origin), for the ⤺ retrace marker. Recomputed each build.
  const _reconsiderOrigins = new Set<string>();
  for (const mm of messages) {
    if ((mm.metadata as any)?.kind === 'council_switchback') {
      const origin = ((mm as any).parent_ids ?? [])[0];
      if (origin) _reconsiderOrigins.add(origin);
    }
  }
  let prevTurnEndId: string | null = null;
  let turnIndex = -1;
  let maxTurnIndex = 0;

  // First pass — count assistant turns for isLatestTurn math.
  for (const m of messages) {
    if (m.role === 'assistant') maxTurnIndex += 1;
  }
  maxTurnIndex = Math.max(0, maxTurnIndex - 1);

  // Edge orientation determines which handles connect. 'spine' runs
  // vertically through the main track (bottom source → top target);
  // 'horizontal' runs sideways for tool branches and peer fans
  // (right source → left target). Matching the rendered geometry
  // keeps smoothstep routing from drawing S-curves through the body
  // of other nodes.
  type EdgeOrient = 'spine' | 'horizontal';
  const addEdge = (
    source: string,
    target: string,
    orient: EdgeOrient = 'spine',
    edgeType: string = 'default',
  ) => {
    const sourceHandle = orient === 'spine' ? 'b' : 'r';
    const targetHandle = orient === 'spine' ? 't' : 'l';
    edges.push({
      id: `${source}->${target}`,
      source,
      target,
      sourceHandle,
      targetHandle,
      type: edgeType,
    });
  };

  // Tracks the id of the most recently encountered user message so
  // each assistant can pair with its own preceding user, not the
  // last user in the whole array (the naive `reverse().find()` lookup
  // breaks for multi-turn sessions).
  let lastUserId: string | null = null;

  // 2026-05-17 — message-id → emitted-node-id map. Lets the peer-author
  // branches (spine_root_marker, peer_response) connect their edges
  // back to whatever node the parent message produced (user_marker,
  // canonical asst_marker, another spine_root_marker, or another
  // peer_response). Without this, peer messages would have no spine
  // anchor and the walker would orphan them.
  const msgIdToNodeId = new Map<string, string>();

  messages.forEach((msg) => {
    if (msg.role !== 'user' && msg.role !== 'assistant') return;

    if (msg.role === 'user') {
      // SP-3a — a suppressed pure-message user turn (its spine child is a peer)
      // renders only in the panel: no canvas node, no edges, no id mapping, and
      // do NOT advance prevTurnEndId (it must keep pointing at the last visible
      // spine node so a dangling edge is never created).
      if (suppressMember(msg)) {
        lastUserId = msg.id;
        return;
      }
      const userId = `user-${msg.id}`;
      msgIdToNodeId.set(msg.id, userId);
      const userNode: Node = {
        id: userId,
        type: 'turn_marker',
        data: {
          kind: 'turn_marker',
          role: 'user',
          preview: (msg.content ?? '').slice(0, 80),
          fullContent: msg.content ?? '',
          messageId: msg.id,
          timestamp: msg.timestamp,
          trailId: ((msg.metadata as any)?.trail_id ?? null),
          turnIndex: Math.max(0, turnIndex + 1),
          isLatestTurn: false, // fixed up when the assistant lands
          reconsideredOrigin: _reconsiderOrigins.has(msg.id),
          turnKey: msg.id,     // user marker's turnKey is its own id
        } as TurnMarkerData,
        position: { x: 0, y: 0 },
        // Explicit width/height bypass RF 11's measurement-gate → wrapper
        // visibility stays at `visible` instead of being reset on every
        // nodes-prop update. Matches DIM_FALLBACK in layoutDag.ts so
        // layout positioning is unchanged. (F4 fix per spec §11.)
        width: 260,
        height: 120,
      };
      nodes.push(userNode);
      // SP-3a — an intent-spine member user turn threads from its REAL
      // parent (parent_ids[0]), not prevTurnEndId. A peer_response is a
      // lateral leaf that never advances prevTurnEndId, so the unconditional
      // chain reparented every reply onto the pre-spine main trail (the
      // original "reply does nothing" bug). The nearest-spine-root authority
      // already excluded non-blueprint trails.
      const memberRoot = intentMembers.get(msg.id);
      if (memberRoot) {
        (userNode.data as any).intentSpineMember = true;
        (userNode.data as any).intentSpineRootId = memberRoot;
        const parentMsgId = (msg as any).parent_ids?.[0] as string | undefined;
        const realParentNodeId = parentMsgId ? msgIdToNodeId.get(parentMsgId) : undefined;
        if (realParentNodeId) addEdge(realParentNodeId, userId);
      } else if (prevTurnEndId) {
        addEdge(prevTurnEndId, userId);
      }
      prevTurnEndId = userId;
      lastUserId = msg.id;
      return;
    }

    // Assistant turn.
    turnIndex += 1;
    const tIdx = turnIndex;

    // v0.2 T4.3 — phantom user_marker synthesis DELETED. Was the v0.1
    // Item 8 band-aid for orphan-asst scenarios (peer-spine extensions,
    // council-approved fork chains). The new v0.2 walker (topologyWalk)
    // treats orphan asst as a spineAnchor; phantom user markers are no
    // longer needed for positioning.
    const userMessageId = lastUserId ?? `unknown-${msg.id}`;
    const latest = isLatestTurnByIndex(
      tIdx,
      maxTurnIndex,
      runtime.inFlightTurnId,
      userMessageId,
    );

    // v0.3 (2026-05-17) — classifier wiring. The Phase 2 cutover
    // shipped a `classifyAssistant` registry (classifyMessage.ts) +
    // four new node kinds (spine_root_marker, peer_response, etc.)
    // but never wired the classifier into the data processor —
    // dead code for the entire v0.2 lifetime. As a result, peer-
    // author messages (e.g. approved propose_fork resolutions) fell
    // to the generic turn_marker + llm_round + asst_marker branch
    // and rendered as ordinary linear-chained asst bubbles. The
    // backend stamps `metadata.is_spine_root: true` on propose_fork
    // approvals; this branch lifts those messages to
    // `spine_root_marker` (ANCHORS group → own column in the
    // walker) so forks visibly fork. Other peer messages render as
    // `peer_response` (LATERAL leaf with peer-tinted chrome).
    const classifiedKind = classifyAssistant(msg);

    if (classifiedKind === 'squad_merge') {
      const md = (msg.metadata as any) || {};
      const parentMsgId = (msg as any).parent_ids?.[0] as string | undefined;
      const parentNodeId = parentMsgId ? msgIdToNodeId.get(parentMsgId) : undefined;
      const mergeId = `squad-merge-${msg.id}`;   // msg.id = asst-synth-{squad_id} → dedup by squad
      msgIdToNodeId.set(msg.id, mergeId);
      const mergeContent = msg.content ?? '';
      nodes.push({
        id: mergeId,
        type: 'squad_merge',
        data: {
          kind: 'squad_merge',
          squadId: md.squad_id,
          synthPreview: mergeContent.slice(0, 160),
          fullContent: mergeContent,
          memberCount: md.member_count ?? 0,
          reportedCount: md.reported_count ?? 0,
          citations: (md.citations ?? []).map((c: any) => ({
            personaId: c.persona_id,
            spineRootMessageId: c.spine_root_message_id,
            stance: c.stance,
          })),
          failedCount: Math.max(0, (md.member_count ?? 0) - (md.reported_count ?? 0)),
          messageId: msg.id,
          turnIndex: tIdx,
          isLatestTurn: latest,
          turnKey: msg.id,
          trailId: ((msg.metadata as any)?.trail_id ?? null),
        } as SquadMergeData,
        position: { x: 0, y: 0 },
        // Explicit dims — RF 11 measurement-gate (feedback_reactflow_node_dims_required).
        width: 280,
        height: 150,
      });
      if (parentNodeId) addEdge(parentNodeId, mergeId);
      return;   // skip canonical asst flow — the merge IS the message
    }

    if (classifiedKind === 'merge_node') {
      const md = (msg.metadata as any) || {};
      const mergeId = `merge-node-${msg.id}`;
      msgIdToNodeId.set(msg.id, mergeId);
      const content = msg.content ?? '';
      const parentIds = ((msg as any).parent_ids ?? []) as string[];
      nodes.push({
        id: mergeId,
        type: 'merge_node',
        data: {
          kind: 'merge_node',
          synthPreview: content.slice(0, 160),
          fullContent: content,
          mergedFrom: (md.merged_from ?? []).map((e: any) => ({
            campId: e.camp_id,
            personaId: e.persona_id ?? null,
            branchTailId: e.branch_tail_id,
          })),
          messageId: msg.id,
          turnIndex: tIdx,
          isLatestTurn: latest,
          turnKey: msg.id,
          trailId: ((msg.metadata as any)?.trail_id ?? null),
          costTelemetry: ((msg.metadata as any)?.cost_telemetry ?? null),  // G4b
        } as MergeNodeData,
        position: { x: 0, y: 0 },
        // Explicit dims — RF 11 measurement-gate (feedback_reactflow_gotchas).
        width: 280,
        height: 150,
      });
      // B1 — one edge per parent. parent_ids[0] = main spine; parent_ids[1..] =
      // camp tails. BOTH use 'spine' orient → both target the merge's TOP handle
      // ('t'), so the camp edge (the camp sits in a right-hand persona column)
      // curves cleanly DOWN into the node instead of wrapping around a left handle
      // (review SHOULD-FIX). The 'merge' edge TYPE (not the handle) carries the amber
      // styling AND lets repositionMergeNodes pick the main parent (e.type!=='merge').
      // Both parents are already in msgIdToNodeId (the merge is appended LAST in
      // document order; a Camp's camp_id ≡ its spine_root marker msg.id).
      // C2.3/B2 — for a council_switchback, parent_ids[0] is the reconsider ORIGIN
      // (an earlier node), so its edge is a real 'reconsider' edge (dashed warm-
      // amber). 'reconsider' !== 'merge', so it stays repositionMergeNodes' main
      // parent → the origin-column/lane placement holds.
      const isSwitchback = (msg.metadata as any)?.kind === 'council_switchback';
      parentIds.forEach((pid, i) => {
        const parentNodeId = msgIdToNodeId.get(pid);
        if (!parentNodeId) return;
        if (i === 0) addEdge(parentNodeId, mergeId, 'spine', isSwitchback ? 'reconsider' : 'default');
        else addEdge(parentNodeId, mergeId, 'spine', 'merge');
      });
      return;  // the merge IS the message — skip the canonical asst flow
    }

    if (classifiedKind === 'spine_root_marker' || classifiedKind === 'peer_response') {
      const personaId: string =
        (msg.metadata as any)?.persona_id ??
        (msg.metadata as any)?.personaId ??
        '';
      const content = msg.content ?? '';
      const parentMsgId = (msg as any).parent_ids?.[0] as string | undefined;
      const parentNodeId = parentMsgId ? msgIdToNodeId.get(parentMsgId) : undefined;

      if (classifiedKind === 'spine_root_marker') {
        const srId = `spine-root-${msg.id}`;
        msgIdToNodeId.set(msg.id, srId);
        // SP-3b — read the generic kind key FIRST (artifact roots stamp
        // spawn_source_kind, NOT the legacy blueprint-only spawn_source;
        // blueprint roots set BOTH so `??` is value-identical there).
        const _kind = (msg.metadata as any)?.spawn_source_kind
          ?? (msg.metadata as any)?.spawn_source;
        nodes.push({
          id: srId,
          type: 'spine_root_marker',
          data: {
            kind: 'spine_root_marker',
            personaId,
            spineRootMessageId: msg.id,
            invitedAt: msg.timestamp,
            task: (msg.metadata as any)?.task,        // sp4 — the invited brief
            forkOriginId: parentMsgId,                // sp4 — mirror of parent_ids[0]
            arrivalState: _mergedCampIds.has(msg.id)
              ? 'merged'
              : campArrivalState(msg, _childrenByParent, _byId),  // sp5 + C2.2
            turnFailedReason: (msg.metadata as any)?.turn_failed_reason,     // sp5
            turnIndex: tIdx,
            isLatestTurn: latest,
            messageId: msg.id,
            turnKey: msg.id,
            trailId: ((msg.metadata as any)?.trail_id ?? null),
            resolvedTarget: resolveTarget?.(personaId, msg.id) ?? undefined,  // SP3a
            // SP2a/SP-3b — source-card spine: the card roots this spine; the FE
            // emits a synthetic card->root edge + pins it below the card.
            spawnSource: _kind,
            sourceManifestId: (msg.metadata as any)?.source_manifest_id,
            sourceArtifactId: (msg.metadata as any)?.spawn_source_id,  // SP-3b
            // SP-2/SP-3b — any source-card root renders as the card's lane-head
            // band (a bottom-attached band under the card), not a free chip.
            spineRole: _kind ? 'card-lane-head' : undefined,
          } as SpineRootMarkerData,
          position: { x: 0, y: 0 },
          // Explicit dims (DIM_FALLBACK) — a culled unmeasured node blocks
          // EVERY RF11 fitView (returns false) + holds the measurement-gate
          // visibility:hidden. Gotcha #4's sweep missed this processor's
          // push sites (field 2026-06-12, turn-end widen no-op).
          width: 180,
          height: 68,
        });
        if (parentNodeId) addEdge(parentNodeId, srId, 'spine', 'fork');
      } else {
        // SP-3a — a foreman peer_response inside the intent spine renders only
        // in the panel: drop the canvas node + its parent edge (no id mapping
        // either, so a kept child can't accidentally edge to a ghost). Skip the
        // canonical-asst flow below via the existing `return`.
        if (suppressMember(msg)) {
          return;
        }
        const prId = `peer-response-${msg.id}`;
        msgIdToNodeId.set(msg.id, prId);
        const peerNode: Node = {
          id: prId,
          type: 'peer_response',
          data: {
            kind: 'peer_response',
            personaId,
            squadId: (msg.metadata as any)?.squad_id,
            stance: (msg.metadata as any)?.stance,
            chorusStance: (msg.metadata as any)?.chorus_stance,   // TH-4: read's declared stance
            content,
            preview: content.slice(0, 80),
            fullContent: content,
            timestamp: msg.timestamp,
            turnIndex: tIdx,
            isLatestTurn: latest,
            messageId: msg.id,
            turnKey: msg.id,
            trailId: ((msg.metadata as any)?.trail_id ?? null),
            resolvedTarget:
              resolveTarget?.(personaId, nearestSpineRootId(msg.id, _byId)) ?? undefined,  // SP3a
            costTelemetry: ((msg.metadata as any)?.cost_telemetry ?? null),  // G4b
          } as PeerResponseData,
          position: { x: 0, y: 0 },
          width: 280,   // DIM_FALLBACK peer_response — see spine_root_marker note
          height: 120,
        };
        nodes.push(peerNode);
        // SP-3a — stamp membership (a foreman peer_response inside the intent
        // spine). Task 5 suppresses the canvas node for these; the stamp marks
        // it for the thread + membership authority.
        const peerMemberRoot = intentMembers.get(msg.id);
        if (peerMemberRoot) {
          (peerNode.data as any).intentSpineMember = true;
          (peerNode.data as any).intentSpineRootId = peerMemberRoot;
        }
        if (parentNodeId) addEdge(parentNodeId, prId);
      }
      // Skip the canonical asst flow below — peer messages have no
      // rounds, tool_calls, or asst_marker; they ARE the message.
      // Also do NOT update prevTurnEndId — peer subgraphs branch off
      // the main spine but don't extend it, so the next user message's
      // user_marker continues from the prior main-spine asst.
      return;
    }

    // SP-1 A8 — a squad message reaching the canonical (turn_marker) flow
    // means it lost its envelope or the classifier missed it. Fire a
    // rate-limited diagnostic so the silent-render class is observable.
    if ((msg.metadata as any)?.squad_id) {
      chatDagWarn('squad_render_gap', msg.id, {
        squadId: (msg.metadata as any).squad_id,
        kind: (msg.metadata as any).kind,
      });
    }

    // Retag the most recent user marker's turnIndex to match this
    // assistant's turn so layout grouping (which indexes by turnIndex)
    // sees both halves of the turn together.
    const lastUserNode = [...nodes]
      .reverse()
      .find((n) => n.type === 'turn_marker' && (n.data as any).role === 'user');
    if (lastUserNode) (lastUserNode.data as any).turnIndex = tIdx;

    const toolCalls = (msg.metadata?.tool_calls ?? []) as ToolCallEntry[];
    const roundsMap = new Map<number, ToolCallEntry[]>();
    toolCalls.forEach((call) => {
      const r = Number(call.timing?.llm_round ?? 1);
      if (!roundsMap.has(r)) roundsMap.set(r, []);
      roundsMap.get(r)!.push(call);
    });
    const sortedRounds: Array<[number, ToolCallEntry[]]> =
      roundsMap.size > 0
        ? Array.from(roundsMap.entries()).sort(([a], [b]) => a - b)
        : [[1, []]];
    const lastRoundIdx = sortedRounds.length - 1;
    // ② — a pure-solo answer (no tool calls → no peers, one synthetic round)
    // does not earn its own llm_round: the asst marker already carries the
    // preview + cost, so an extra "Synthesis ✓" card is a redundant double-
    // render. Suppress the round; the spine closes user → asst.
    const isPureSolo = toolCalls.length === 0;

    // v0.3 edge topology (rev: untangle Alan's "visual mess" feedback
    // after the Task 16 flag flip): main spine is a clean vertical at
    // x=0 — user → round-1 → round-2 → … → round-N → asst. Tool calls
    // branch RIGHTWARD off their round as LEAVES with no further
    // chain. Peers branch off consult_peers tools as leaves on the
    // polar arc. No fan-in back to the assistant marker from peers or
    // tools — the spine closes via the last round only.
    const turnStartSpine = prevTurnEndId; // user marker (or prev asst on multi-turn)
    let prevRoundInTurn: string | null = null;

    // Collapsed phase stack: skip rounds/tools/peers emission entirely,
    // emit a single card between user and asst markers.
    if (phaseStackCollapsed(runtime, userMessageId)) {
      const userMsg = messages.find((m) => m.id === userMessageId);
      const ttMeta = (msg.metadata as any)?.turn_telemetry;
      // Count total peers across all consult_peers tool calls.
      let peerCount = 0;
      for (const call of toolCalls) {
        if (toolName(call) !== 'consult_peers') continue;
        const inner = unwrapResult(call);
        const peers: any[] = Array.isArray(inner?.responses)
          ? inner.responses
          : Array.isArray(inner?.peers)
            ? inner.peers
            : Array.isArray(inner?.results)
              ? inner.results
              : [];
        peerCount += peers.length;
      }
      const stackId = `stack-${userMessageId}`;
      nodes.push({
        id: stackId,
        type: 'collapsed_phase_stack',
        data: {
          kind: 'collapsed_phase_stack',
          turnKey: userMessageId,
          turnIndex: tIdx,
          name: deriveStackName(userMsg?.content),
          roundCount: sortedRounds.length,
          toolCount: toolCalls.length,
          peerCount,
          totalCostUsd: ttMeta?.estimated_cost_usd ?? null,
          totalTokens: ttMeta?.total_tokens ?? null,
          isLatestTurn: latest,
          messageId: msg.id,
        } as CollapsedPhaseStackData,
        position: { x: 0, y: 0 },
        width: 320,   // DIM_FALLBACK collapsed_phase_stack — see spine_root_marker note
        height: 90,
      });
      if (turnStartSpine) addEdge(turnStartSpine, stackId);
      prevRoundInTurn = stackId;
    } else if (!isPureSolo) {

    sortedRounds.forEach(([round, calls], i) => {
      // ② — "Synthesis" means a round that combined PRIOR rounds/peers. A
      // lone round (the only round of the turn) synthesizes nothing — it is
      // the "Answer" round. The terminal round still drives the delivery-card
      // enrichment below (gated on isSynthesis); only the *label* differs.
      const isTerminalRound = i === lastRoundIdx;
      const isSynthesis = isTerminalRound && lastRoundIdx > 0;
      const isLoneAnswer = isTerminalRound && lastRoundIdx === 0;
      // v0.2 T2.5 flip: round-id derives from asst msg.id (NOT user id).
      // Fixes v0.1 limitation #2 — multi-asst-under-one-user trees
      // collided on `round-${userMsgId}-${round}` between siblings.
      // Speculative path (runtimeOverlay.emitSpeculativeTurn) still
      // uses userMessageId because the asst doesn't exist yet there;
      // mismatch is transient (resolves when inFlightTurnId clears).
      const roundId = `round-${msg.id}-${round}`;
      // Synthesis-only enrichment — turn totals + answer preview from
      // the asst message metadata. Lets the synthesis node render as
      // a "delivery card" instead of stopping at "writing the answer."
      const _ttMeta = isSynthesis ? (msg.metadata as any)?.turn_telemetry : null;
      const _answerStr = isSynthesis ? (msg.content ?? '') : '';
      // Slightly bigger preview now that the node uses MarkdownPreview
      // (which clips by max-height); ~180 chars feeds 3-4 lines comfortably.
      const _answerPreview = isSynthesis && _answerStr.length > 0
        ? (_answerStr.length > 180 ? `${_answerStr.slice(0, 180).trimEnd()}…` : _answerStr)
        : undefined;
      // Non-synth rounds: surface which tools fanned out, by name +
      // call order. Drives the "→ search · roster · …" at-a-glance
      // line on the round node.
      const _toolNames = isSynthesis
        ? undefined
        : calls.map((c) =>
            (c?.identity?.tool_name ?? c?.tool_name ?? '').replace(/^admin_/, ''),
          ).filter((s) => s.length > 0);
      nodes.push({
        id: roundId,
        type: 'llm_round',
        data: {
          kind: 'llm_round',
          turnId: msg.id,
          round,
          modelUsed: (msg.metadata as any)?.model,
          isSynthesis,
          isLoneAnswer,
          toolCallCount: calls.length,
          runState: 'completed',
          turnTotalTokens: isSynthesis ? (_ttMeta?.total_tokens ?? null) : undefined,
          turnTotalCostUsd: isSynthesis ? (_ttMeta?.estimated_cost_usd ?? null) : undefined,
          turnDurationMs: isSynthesis ? (_ttMeta?.total_duration_ms ?? null) : undefined,
          turnAnswerPreview: _answerPreview,
          turnTimestamp: isSynthesis ? msg.timestamp : undefined,
          toolNames: _toolNames,
          turnIndex: tIdx,
          isLatestTurn: latest,
          messageId: msg.id,
          turnKey: userMessageId,
        } as LlmRoundData,
        position: { x: 0, y: 0 },
        // F4 fix: explicit dims bypass RF measurement-gate. See user-marker emit above.
        width: 220,
        height: 95,
      });
      // Spine edge: previous round (or turn-start) → this round
      const spineParent = prevRoundInTurn ?? turnStartSpine;
      if (spineParent) addEdge(spineParent, roundId);

      calls.forEach((call, ci) => {
        const tcId = `tc-${toolCallId(call, `${msg.id}-${round}-${ci}`)}`;
        nodes.push({
          id: tcId,
          type: 'tool_call',
          data: {
            kind: 'tool_call',
            call,
            messageId: msg.id,
            round,
            runState: 'completed',
            turnIndex: tIdx,
            isLatestTurn: latest,
            turnKey: userMessageId,
          } as ToolCallDagData,
          position: { x: 0, y: 0 },
          // F4 fix: explicit dims bypass RF measurement-gate.
          width: 280,
          height: 90,
        });
        addEdge(roundId, tcId, 'horizontal');

        // consult_peers peer children — canonical expand path, with
        // the FanCollapsedChip substitution gated by fanCollapsed().
        // Peers are LEAVES — no fan-in to the assistant marker.
        if (toolName(call) === 'consult_peers') {
          const inner = unwrapResult(call);
          const rawPeers: any[] = Array.isArray(inner?.responses)
            ? inner.responses
            : Array.isArray(inner?.peers)
              ? inner.peers
              : Array.isArray(inner?.results)
                ? inner.results
                : [];
          const parentToolCallId = toolCallId(
            call,
            `${msg.id}-${round}-${ci}`,
          );
          const collapsed =
            !!runtime.allFansCollapsed ||
            fanCollapsed(runtime.collapsedFanIds, parentToolCallId);

          if (collapsed && rawPeers.length > 0) {
            const okCount = rawPeers.filter((p) => p?.status === 'ok').length;
            const offlineCount = rawPeers.filter(
              (p) => p?.status === 'offline',
            ).length;
            const errorCount = rawPeers.filter(
              (p) =>
                p?.status === 'error' ||
                p?.status === 'timeout' ||
                p?.status === 'budget_exceeded',
            ).length;
            const totalDurationMs =
              rawPeers.reduce(
                (sum, p) =>
                  sum + (typeof p?.duration_ms === 'number' ? p.duration_ms : 0),
                0,
              ) || null;
            const rInner = inner as any;
            const totalTokens =
              rInner?.total_usage?.total_tokens ?? null;

            const chipId = `${tcId}-chip`;
            nodes.push({
              id: chipId,
              type: 'fan_collapsed_chip',
              data: {
                kind: 'fan_collapsed_chip',
                parentToolCallId,
                peerCount: rawPeers.length,
                okCount,
                offlineCount,
                errorCount,
                totalDurationMs,
                totalTokens,
                turnIndex: tIdx,
                isLatestTurn: latest,
                messageId: msg.id,
                turnKey: userMessageId,
              },
              position: { x: 0, y: 0 },
              width: 220,   // DIM_FALLBACK fan_collapsed_chip — see spine_root_marker note
              height: 70,
            });
            addEdge(tcId, chipId, 'horizontal');
          } else {
            // Shared consult_peers prompt + context — same for every
            // peer in the fan — pulled off the call's args. Peer
            // actually received `context + "\n\n" + prompt` as its
            // user_message (see consult_peers_tool.py _invoke_peer).
            // coerceContentToString tolerates the backend truncation
            // stub (legacy 512B leaf cap) so older turns still render
            // a placeholder instead of nothing.
            const _rawPrompt = (call.arguments as any)?.prompt;
            const consultPrompt = (() => {
              const s = coerceContentToString(_rawPrompt);
              return s.length > 0 ? s : undefined;
            })();
            const _rawContext = (call.arguments as any)?.context;
            const consultContext = (() => {
              const s = coerceContentToString(_rawContext);
              return s.length > 0 ? s : undefined;
            })();
            rawPeers.forEach((peer, pi) => {
              const personaId = peer?.peer_persona_id ?? peer?.persona_id;
              const personaName =
                peer?.peer_persona_name ?? peer?.persona_name ?? peer?.name;
              const peerId = `${tcId}-peer-${personaId ?? pi}`;
              const usage = peer?.usage ?? {};
              nodes.push({
                id: peerId,
                type: 'peer',
                data: {
                  kind: 'peer',
                  personaName,
                  personaId,
                  status: peer?.status ?? 'ok',
                  durationMs: peer?.duration_ms,
                  response: coerceContentToString(peer?.response ?? peer?.content),
                  parentToolCallId: tcId.replace(/^tc-/, ''),
                  runState: 'completed',
                  turnIndex: tIdx,
                  isLatestTurn: latest,
                  messageId: msg.id,
                  turnKey: userMessageId,
                  prompt: consultPrompt,
                  context: consultContext,
                  model: peer?.model,
                  error: peer?.error,
                  promptTokens: usage?.prompt_tokens,
                  completionTokens: usage?.completion_tokens,
                  totalTokens: usage?.total_tokens,
                  costUsd: usage?.cost,
                  // Per-peer resolver telemetry from consult_peers —
                  // tells operator how this specific peer got picked.
                  matchedVia: peer?.matched_via,
                  matchedTag: peer?.matched_tag ?? null,
                  matchScore: peer?.match_score,
                  candidateCount: peer?.candidate_count,
                  matchQueries: Array.isArray(peer?.match_queries)
                    ? (peer.match_queries as string[])
                    : peer?.match_query
                      ? [peer.match_query as string]
                      : undefined,
                } as PeerDagData,
                position: { x: 0, y: 0 },
                // F4 fix: explicit dims bypass RF measurement-gate.
                width: 240,
                height: 140,
              });
              addEdge(tcId, peerId, 'horizontal');
            });
          }
        }
      });

      prevRoundInTurn = roundId;
    });
    } // end: normal (not-collapsed) phase-stack emission branch

    const asstId = `asst-${msg.id}`;
    msgIdToNodeId.set(msg.id, asstId);
    const ttMeta = (msg.metadata as any)?.turn_telemetry;
    const asstNode: Node = {
      id: asstId,
      type: 'turn_marker',
      data: {
        kind: 'turn_marker',
        role: 'assistant',
        preview: (msg.content ?? '').slice(0, 80),
        fullContent: msg.content ?? '',
        modelUsed: (msg.metadata as any)?.model,
        messageId: msg.id,
        timestamp: msg.timestamp,
        totalCostUsd: ttMeta?.estimated_cost_usd ?? null,
        totalTokens: ttMeta?.total_tokens ?? null,
        toolCallCount: toolCalls.length,
        turnIndex: tIdx,
        isLatestTurn: latest,
        reconsideredOrigin: _reconsiderOrigins.has(msg.id),
        turnKey: userMessageId,
        trailId: ((msg.metadata as any)?.trail_id ?? null),
        primaryPersona: runtime.primaryPersona ?? null,
      } as TurnMarkerData,
      position: { x: 0, y: 0 },
      // F4 fix: explicit dims bypass RF measurement-gate.
      width: 260,
      height: 120,
    };
    nodes.push(asstNode);
    // SP-3a — a canonical-asst member turn stays fully on the canvas
    // (provenance intact, approach B); stamp membership so the layout pass
    // folds it under the collapsed card and its text mirrors into the panel.
    const asstMemberRoot = intentMembers.get(msg.id);
    if (asstMemberRoot) {
      (asstNode.data as any).intentSpineMember = true;
      (asstNode.data as any).intentSpineRootId = asstMemberRoot;
    }
    // Spine closes: last round → asst (or turn-start → asst if the
    // turn had no rounds at all — edge case but well-defined).
    const asstSpineParent = prevRoundInTurn ?? turnStartSpine;
    if (asstSpineParent) addEdge(asstSpineParent, asstId);
    prevTurnEndId = asstId;
  });

  // Task 12: if a turn is in flight (user sent but no assistant landed
  // yet), synthesize speculative nodes from runtime.livePhaseEvents so
  // the DAG reflects "thinking…" state during long tool runs.
  emitSpeculativeTurn(nodes, edges, messages, runtime);

  // Task 13: reconcile sweeps.
  orphanRunningSweep(nodes, messages);
  errorTurnSweep(nodes, runtime);

  // Trailmap L1 (plan Task 13) — emit artifact_tile nodes + orphan_tray
  // when /dag fetch has populated state.dag. Backward-compatible: when
  // dag is undefined (existing callers, tests), this is a no-op.
  if (dag) {
    _emitArtifactTilesAndOrphans(
      nodes, edges, dag, messages, onTileClick,
      runtime.expandedArtifactTurnIds, runtime.activeDim,
      runtime.expandedArtifactIds, artifactContent,
      {
        onAsk: runtime.onAskArtifact,
        askInFlight: runtime.askInFlight,
        askError: runtime.askError,
        onSeedSpineExpanded: runtime.onSeedSpineExpanded,
        onToggleSpine: runtime.onToggleSpine,
      },
    );
  }

  // Living Blueprint SP1 — emit the summoned blueprint flip card (trail-level
  // decoration). Positioned at origin here; decorationPass (layoutGroups)
  // repositions to the trail upper-left. Explicit footprint (RF11 fitView
  // gotcha #4 — unmeasured nodes break fitView).
  if (runtime.blueprintManifestId) {
    nodes.push({
      id: `blueprint-${runtime.blueprintManifestId}`,
      type: 'artifactory_blueprint',
      position: { x: 0, y: 0 },
      width: BLUEPRINT_NODE_SIZE.w,
      height: BLUEPRINT_NODE_SIZE.h,
      data: {
        kind: 'artifactory_blueprint',
        manifestId: runtime.blueprintManifestId,
        onOpen: runtime.onOpenBlueprint,
        onGenerate: runtime.onGenerateBlueprint,
      },
      selectable: true,
      draggable: false,
    } as any);

    // SP2a — the card ROOTS its intent spine: emit a synthetic fork edge from
    // the card to the intent-spine root. The root's parent_ids=['blueprint-<mid>']
    // is NOT a message, so the normal edge-build (msgIdToNodeId) never wires it.
    const intentRoot = nodes.find(
      (n) => n.type === 'spine_root_marker'
        && (n.data as any)?.spawnSource === 'blueprint_intent'
        && (n.data as any)?.sourceManifestId === runtime.blueprintManifestId,
    );
    if (intentRoot) {
      edges.push({
        id: `bp-intent-${runtime.blueprintManifestId}`,
        source: `blueprint-${runtime.blueprintManifestId}`,
        target: intentRoot.id,
        type: 'fork',
        sourceHandle: 'b',
        targetHandle: 't',
      } as any);
    }
  }

  // SP-3b — clone the blueprint synthetic-edge for artifact spines. The artifact
  // root's parent_ids[0]='tile-{aid}' is a NODE id (not a message), so the normal
  // edge-build never wires it. A SEPARATE loop (NOT a widen of the blueprint block,
  // whose predicate keys on manifestId). Runs after _emitArtifactTilesAndOrphans
  // (:851) so the tile-{aid} nodes already exist.
  for (const n of nodes) {
    if (n.type !== 'spine_root_marker') continue;
    if ((n.data as any)?.spawnSource !== 'artifact') continue;
    const aid = (n.data as any)?.sourceArtifactId;
    if (!aid) continue;
    const tileId = `tile-${aid}`;
    if (!nodes.find((t) => t.id === tileId)) continue; // tile not rendered → skip
    edges.push({
      id: `art-spine-${aid}`,
      source: tileId,
      target: n.id,
      type: 'fork',
      sourceHandle: 'b',
      targetHandle: 't',
    } as any);
  }

  // ChatDAGRAGDAG Sub-plan 3 Task G.4 (SP3-MF-05) — second-pass reference
  // edge emission per spec §5.3. Squad-member spine roots (P3 pattern)
  // carry metadata.reference_node_ids pointing back at the source nodes
  // the operator asked them to examine. Insertion AFTER
  // _emitArtifactTilesAndOrphans so artifact-tile node ids are already
  // in the rendered node set when we look up source-by-id.
  for (const msg of messages) {
    const md = (msg as any).metadata || {};
    if (md.request_kind === 'P3_examine_then_dag'
        && Array.isArray(md.reference_node_ids)) {
      // A6 (SP-1) — resolve the TARGET (this member's spine node) through the
      // map; raw msg.id never matches a node id (peer-response-${id}).
      const targetNodeId = msgIdToNodeId.get(msg.id);
      if (!targetNodeId) continue;
      for (const sourceId of md.reference_node_ids as string[]) {
        if (!sourceId) continue;
        // A6 — resolve the SOURCE too; fall back to the raw id for sources
        // that are already node ids (e.g. artifact-tile ids).
        const sourceNodeId = msgIdToNodeId.get(sourceId) ?? sourceId;
        if (!nodes.find((n) => n.id === sourceNodeId)) continue;
        edges.push({
          id: `ref-${sourceNodeId}-${targetNodeId}`,
          source: sourceNodeId,
          target: targetNodeId,
          type: 'reference',
          data: {
            kind: 'reference',
            sourceNodeId,
            targetSpineRootId: targetNodeId,
            squadId: md.squad_id,
          },
        } as Edge);
      }
    }
  }

  // v0.1 Item 8 — pre-user orphan negative-y fallback REMOVED.
  // Phantom user_marker synthesis in the asst branch above gives every
  // orphan asst its own turn, so layoutChatDag positions them all in
  // a natural top-to-bottom chronological flow. The post-layout sweep
  // in ChatDagCanvas remains as a safety net for any edge case.

  // Squad fold-back SP-1 (A3) — decorative member→merge reference edges. The
  // merge's LAYOUT parent is the anchor (single-parent, §0.7); these post-
  // positioned edges visually connect each cited member spine to the merge
  // without driving layout.
  for (const sMsg of messages) {
    const md = (sMsg as any).metadata || {};
    if (md.kind !== 'squad_synth') continue;
    const mergeNodeId = msgIdToNodeId.get(sMsg.id);
    if (!mergeNodeId) continue;
    for (const c of (md.citations ?? [])) {
      const memberNodeId = c?.spine_root_message_id
        ? msgIdToNodeId.get(c.spine_root_message_id)
        : undefined;
      if (!memberNodeId || !nodes.find((n) => n.id === memberNodeId)) continue;
      edges.push({
        id: `merge-ref-${memberNodeId}-${mergeNodeId}`,
        source: memberNodeId,
        target: mergeNodeId,
        type: 'reference',
        data: {
          kind: 'reference',
          sourceNodeId: memberNodeId,
          targetSpineRootId: mergeNodeId,
          squadId: md.squad_id,
        },
      } as Edge);
    }
  }

  // SP-3a/SP-3b — emit ONE spine_chat_panel per source-card spine (blueprint
  // OR artifact) that has any messages. A dumb renderer: the processor pre-builds
  // the ordered thread; the panel reuses ConversationContext for live send/pending
  // state. The panel carries ZERO canvas edges — positionIntentSpine pins it below
  // its card (Task B4). cardId is resolved PER KIND so each panel pins under its
  // own card; NO blueprint gate (an artifact spine needs a panel even with no
  // blueprint bound). A panel with no resolvable card is skipped (no home).
  const artifactById = new Map((dag?.artifacts ?? []).map((a) => [a.id, a]));
  for (const rootId of new Set(intentMembers.values())) {
    const thread = buildIntentThread(messages, intentMembers, rootId);
    const rootNode = nodes.find((n) => n.id === `spine-root-${rootId}`);
    const kind = (rootNode?.data as any)?.spawnSource;
    // SP-3b — an artifact spine renders a COLD-START panel even with an empty
    // thread (buildIntentThread excludes the root, so a freshly-spawned spine
    // is empty). Blueprint panels still require content (they always have it).
    if (!thread.length && kind !== 'artifact') continue;
    let cardId: string | null = null;
    let artifactName: string | undefined;
    let artifactType: string | undefined;
    if (kind === 'blueprint_intent' && runtime.blueprintManifestId) {
      cardId = 'blueprint-' + runtime.blueprintManifestId;
    } else if (kind === 'artifact') {
      const aid = (rootNode?.data as any)?.sourceArtifactId;
      if (aid) {
        cardId = `tile-${aid}`;
        const art = artifactById.get(aid);
        artifactName = art?.name;
        artifactType = art?.type;
      }
    }
    if (!cardId) continue; // no resolvable card → no home for the panel
    const foreman = [...thread].reverse().find((t) => t.role === 'foreman');
    const hasOperatorTurns = thread.some((t) => t.role === 'user');
    nodes.push({
      id: `intent-panel-${rootId}`,
      type: 'spine_chat_panel',
      position: { x: 0, y: 0 },
      width: 340,
      height: 420,
      data: {
        kind: 'spine_chat_panel',
        cardId,
        spineRootMessageId: rootId,
        thread,
        personaId: foreman?.personaId ?? null,
        residentNamesByPersona: {}, // filled by the panel from context (Task 8/C5)
        hasOperatorTurns,
        artifactName,                // SP-3b — cold-start header (C5)
        artifactType,
        hostPersonaName: null,       // SP-3b — filled by the panel from context (C5)
      },
    } as any);
    // Mirror the operator-touched flag onto the card so F5 default-expands it.
    const cardNode = nodes.find((n) => n.id === cardId);
    if (cardNode) (cardNode.data as any).hasOperatorTurns = hasOperatorTurns;
  }

  return { nodes, edges };
}
